import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";

const InfoTooltip = ({ children, className = "" }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, placement: 'bottom', align: 'center' });
  const iconRef = useRef(null);
  const tooltipRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (iconRef.current && showTooltip) {
      const rect = iconRef.current.getBoundingClientRect();
      const tooltipWidth = 288; // w-72 = 288px
      const spacing = 8; // gap between icon and tooltip
      
      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Calculate available space on each side
      const spaceLeft = rect.left;
      const spaceRight = viewportWidth - rect.right;
      const spaceTop = rect.top;
      const spaceBottom = viewportHeight - rect.bottom;
      
      // Determine horizontal alignment
      let left = rect.left + rect.width / 2;
      let align = 'center';
      
      // If icon is on the left side of the screen (< 200px from left edge)
      if (rect.left < 200) {
        // Align tooltip to the right of the icon
        left = rect.right + spacing + tooltipWidth / 2;
        align = 'left';
      } 
      // If icon is on the right side of the screen (> viewportWidth - 200px)
      else if (rect.right > viewportWidth - 200) {
        // Align tooltip to the left of the icon
        left = rect.left - spacing - tooltipWidth / 2;
        align = 'right';
      }
      // Otherwise center it
      else {
        // Check if tooltip would overflow on the right
        if (left + tooltipWidth / 2 > viewportWidth - 10) {
          left = viewportWidth - 10 - tooltipWidth / 2;
        }
        // Check if tooltip would overflow on the left
        if (left - tooltipWidth / 2 < 10) {
          left = 10 + tooltipWidth / 2;
        }
      }
      
      // Determine vertical placement
      let top = rect.bottom + spacing;
      let placement = 'bottom';
      
      // If not enough space below, show above
      if (spaceBottom < 200 && spaceTop > spaceBottom) {
        top = rect.top - spacing;
        placement = 'top';
      }
      
      setPosition({ top, left, placement, align });
    }
  }, [showTooltip]);

  useEffect(() => {
    if (showTooltip) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showTooltip, updatePosition]);

  // Get transform based on alignment
  const getTransform = () => {
    if (position.align === 'left') {
      return 'translateX(0)';
    } else if (position.align === 'right') {
      return 'translateX(-100%)';
    }
    return 'translateX(-50%)';
  };

  // Get arrow position
  const getArrowStyle = () => {
    const baseStyle = {
      position: 'absolute',
      border: '4px solid transparent',
    };

    // Vertical arrow position
    if (position.placement === 'bottom') {
      baseStyle.top = '-8px';
      baseStyle.borderTopColor = 'transparent';
      baseStyle.borderBottomColor = '#1f2937';
    } else {
      baseStyle.bottom = '-8px';
      baseStyle.borderBottomColor = 'transparent';
      baseStyle.borderTopColor = '#1f2937';
    }

    // Horizontal arrow position based on alignment
    if (position.align === 'left') {
      baseStyle.left = '8px';
      baseStyle.transform = 'none';
    } else if (position.align === 'right') {
      baseStyle.right = '8px';
      baseStyle.left = 'auto';
      baseStyle.transform = 'none';
    } else {
      baseStyle.left = '50%';
      baseStyle.transform = 'translateX(-50%)';
    }

    return baseStyle;
  };

  return (
    <div 
      className={`relative inline-block ${className}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div
        ref={iconRef}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs font-bold cursor-help hover:bg-gray-300 transition-colors"
      >
        ?
      </div>
      {showTooltip && createPortal(
        <div 
          ref={tooltipRef}
          className="fixed z-[9999] w-72 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg"
          style={{
            top: position.top,
            left: position.left,
            transform: getTransform(),
          }}
        >
          {children}
          <div 
            className="absolute border-4 border-transparent"
            style={getArrowStyle()}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default InfoTooltip;